import type { StrategySummary } from "@trader/shared/StrategiesApi"
import { useEffect, useSyncExternalStore } from "react"
import { call } from "../../api.ts"

// The signed-in user's strategies ("My strategies" in the strategy list),
// loaded on demand and refreshed after the editor saves one.

export type MyStrategies =
  | { readonly status: "idle" }
  | { readonly status: "loading"; readonly items: ReadonlyArray<StrategySummary> }
  | { readonly status: "ready"; readonly items: ReadonlyArray<StrategySummary> }
  | { readonly status: "failed"; readonly message: string }

let state: MyStrategies = { status: "idle" }
let generation = 0
const listeners = new Set<() => void>()

const set = (next: MyStrategies) => {
  state = next
  for (const listener of listeners) listener()
}

/** Archived strategies stay on the server (runners may pin them) but leave the list. */
export const visibleStrategies = (items: ReadonlyArray<StrategySummary>): ReadonlyArray<StrategySummary> =>
  items.filter((item) => item.archivedAt === null)

export const myStrategies = {
  refresh: async (): Promise<void> => {
    const mine = ++generation
    set({ status: "loading", items: state.status === "ready" || state.status === "loading" ? state.items : [] })
    try {
      const items = await call((client) => client.strategies.list())
      if (mine === generation) set({ status: "ready", items: visibleStrategies(items) })
    } catch (error) {
      if (mine === generation) set({ status: "failed", message: error instanceof Error ? error.message : String(error) })
    }
  },
  /** Another user signed in. */
  reset: (): void => {
    generation++
    set({ status: "idle" })
  }
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The list, fetched when first shown. */
export const useMyStrategies = (): MyStrategies => {
  const current = useSyncExternalStore(subscribe, () => state)
  useEffect(() => {
    void myStrategies.refresh()
  }, [])
  return current
}
